import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useAuth } from '../context/AuthContext';
import registrationService from '../services/registrationService';
import usfsService from '../services/usfsService';
import { getApiBaseUrlWithFallback } from '../config/environment';
import { ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE } from '../utils/skatingLevels';
import { isSeasonRegistrationOpen } from '../utils/seasonRegistration';
import EligibilityTracker from '../components/EligibilityTracker';
import USFSLinkDialog from '../components/USFSLinkDialog';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EmojiEvents as TrophyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  School as CoachIcon,
  Business as ClubIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckIcon,
  Block as BlockIcon,
  PauseCircle as PauseIcon,
  ExitToApp as WithdrawIcon,
  Refresh as RefreshIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { fetchInterclubs, fetchClubs } from '../services/clubService';

export default function SkaterDetail() {
  const { skaterId } = useParams();
  const navigate = useNavigate();
  const { user, getIdToken, loading: authLoading } = useFirebaseAuth();
  const { showQualifierLink } = useAuth();

  const [skater, setSkater] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeasonId, setSelectedSeasonId] = useState('');
  const [currentSeasonId, setCurrentSeasonId] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(true);
  const [scoreData, setScoreData] = useState(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreError, setScoreError] = useState(null);
  const [previousSeasonResults, setPreviousSeasonResults] = useState(null);
  const [previousSeasonLoading, setPreviousSeasonLoading] = useState(false);

  // Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [interclubs, setInterclubs] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [prePrelimAcknowledged, setPrePrelimAcknowledged] = useState(false);

  // USFS linking state
  const [usfsStatus, setUsfsStatus] = useState(null);
  const [usfsLoading, setUsfsLoading] = useState(true);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [unlinking, setUnlinking] = useState(false);
  const [usfsError, setUsfsError] = useState('');
  const [skaterForm, setSkaterForm] = useState({
    firstName: '',
    lastName: '',
    membershipId: '',
    level: '',
    birthDate: '',
    interclubId: '',
    clubId: '',
    club: '',
    coaches: [],
    notes: ''
  });

  const allLevelOptions = ORDERED_SKATING_LEVELS_FOR_SKATER_PROFILE;

  const loadSkater = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      // Load skater data
      const skatersData = await registrationService.getSkaters(token);
      const skaterData = skatersData.find(s => s.id === skaterId);
      
      if (!skaterData) {
        // Try coached skaters
        const coachedData = await registrationService.getCoachedSkaters(token);
        const coachedSkater = coachedData.find(s => s.id === skaterId);
        if (!coachedSkater) {
          throw new Error('Skater not found');
        }
        setSkater({ ...coachedSkater, isCoachedSkater: true });
      } else {
        setSkater(skaterData);
      }

      // Load seasons
      const baseUrl = await getApiBaseUrlWithFallback();
      const seasonsResponse = await fetch(`${baseUrl}/seasons`);
      let seasonsData = [];
      let currentSeasonFound = null;
      
      if (seasonsResponse.ok) {
        seasonsData = await seasonsResponse.json();
        setSeasons(seasonsData);
        
        // Find current season
        const now = new Date();
        currentSeasonFound = seasonsData.find(s => {
          const startDate = s.startDate?._seconds 
            ? new Date(s.startDate._seconds * 1000) 
            : new Date(s.startDate);
          const endDate = s.endDate?._seconds 
            ? new Date(s.endDate._seconds * 1000) 
            : new Date(s.endDate);
          return now >= startDate && now <= endDate;
        });
        if (currentSeasonFound) {
          setCurrentSeasonId(currentSeasonFound.id);
          setSelectedSeasonId(currentSeasonFound.id);
        } else if (seasonsData.length > 0) {
          setSelectedSeasonId(seasonsData[0].id);
        }
      }

      // Load registrations for current season
      setRegistrationsLoading(true);
      try {
        if (currentSeasonFound) {
          const regsData = await registrationService.getSeasonRegistrations(currentSeasonFound.id, token);
          setRegistrations(regsData);
        }
      } catch (regErr) {
        console.error('Error loading registrations:', regErr);
      } finally {
        setRegistrationsLoading(false);
      }

      // Load coaches (USFS directory only for selection)
      const coachesData = await registrationService.getCoaches(token, { usfsOnly: true });
      setCoaches(coachesData);

    } catch (err) {
      console.error('Error loading skater:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skaterId, getIdToken]);

  useEffect(() => {
    if (!authLoading && user) {
      loadSkater();
    } else if (!authLoading && !user) {
      navigate('/');
    }
  }, [authLoading, user, loadSkater, navigate]);

  useEffect(() => {
    if (selectedSeasonId && skater?.membershipId) {
      loadPreviousSeasonResults();
    }
  }, [selectedSeasonId, skater?.membershipId]);

  const loadClubs = async (interclubId) => {
    try {
      const clubsData = await fetchClubs(interclubId);
      setClubs(clubsData);
    } catch (err) {
      console.error('Error loading clubs:', err);
    }
  };

  const loadInterclubs = async () => {
    try {
      const interclubsData = await fetchInterclubs();
      setInterclubs(interclubsData);
    } catch (err) {
      console.error('Error loading interclubs:', err);
    }
  };

  // USFS Functions - Skater-specific linking
  const loadUSFSStatus = useCallback(async () => {
    if (!skaterId) return;
    try {
      setUsfsLoading(true);
      setUsfsError('');
      const token = await getIdToken(true);
      if (!token) return;
      // Use skater-specific USFS status endpoint
      const status = await usfsService.getSkaterStatus(skaterId, token);
      setUsfsStatus(status);
    } catch (err) {
      console.error('Error loading skater USFS status:', err);
      setUsfsError(err.message || 'Failed to load USFS status');
    } finally {
      setUsfsLoading(false);
    }
  }, [getIdToken, skaterId]);

  useEffect(() => {
    if (!authLoading && user && skaterId) {
      loadUSFSStatus();
    }
  }, [authLoading, user, skaterId, loadUSFSStatus]);

  const handleOpenUSFSLink = () => {
    setLinkError('');
    setLinkDialogOpen(true);
  };

  const handleUSFSLink = async ({ username, password, consent }) => {
    try {
      setLinkLoading(true);
      setLinkError('');
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      // Use skater-specific link endpoint
      await usfsService.linkSkaterAccount(skaterId, { username, password, consent }, token);
      setLinkDialogOpen(false);
      await loadUSFSStatus();
      // Reload skater to get updated USFS verification status
      await loadSkater();
      setSuccess('Skater USFS account linked successfully!');
    } catch (err) {
      console.error('Skater USFS link error:', err);
      setLinkError(err.message || 'Failed to link USFS account');
    } finally {
      setLinkLoading(false);
    }
  };

  const handleManualSync = async () => {
    try {
      setUsfsLoading(true);
      setUsfsError('');
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      // Use skater-specific sync endpoint
      await usfsService.triggerSkaterSync(skaterId, token);
      await loadUSFSStatus();
      // Reload skater to get updated data
      await loadSkater();
      setSuccess('Skater USFS data synced successfully!');
    } catch (err) {
      console.error('Skater USFS sync error:', err);
      setUsfsError(err.message || 'Failed to sync USFS data');
    } finally {
      setUsfsLoading(false);
    }
  };

  const handleUnlink = async ({ clearData }) => {
    try {
      setUnlinking(true);
      setUsfsError('');
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }
      // Use skater-specific unlink endpoint
      await usfsService.unlinkSkaterAccount(skaterId, { clearData }, token);
      await loadUSFSStatus();
      // Reload skater to reflect changes
      await loadSkater();
      setSuccess('Skater USFS account unlinked.');
    } catch (err) {
      console.error('Skater USFS unlink error:', err);
      setUsfsError(err.message || 'Failed to unlink USFS account');
    } finally {
      setUnlinking(false);
    }
  };

  const handleEditSkater = async () => {
    await loadInterclubs();
    if (skater.interclubId) {
      await loadClubs(skater.interclubId);
    }

    const seasonLevels = selectedSeasonId ? skater.seasonLevels?.[selectedSeasonId] : null;
    let skaterCoaches = skater.coaches || [];
    if (skaterCoaches.length === 0 && (skater.coachName || skater.coachUserId)) {
      skaterCoaches = [{
        coachId: skater.coachUserId || '',
        displayName: skater.coachName || '',
        email: skater.coachEmail || '',
      }];
    }

    const currentLevel = seasonLevels?.currentLevel || seasonLevels?.startingLevel || skater.currentLevel || skater.startingLevel || skater.level || '';
    setPrePrelimAcknowledged(!!skater.prePrelimAcknowledgedAt);

    // Convert birthDate to YYYY-MM-DD format for the date input
    let formattedBirthDate = '';
    const rawBirthDate = skater.birthDate || skater.dateOfBirth;
    if (rawBirthDate) {
      if (typeof rawBirthDate === 'string') {
        if (/^\d{4}-\d{2}-\d{2}$/.test(rawBirthDate)) {
          formattedBirthDate = rawBirthDate;
        } else if (rawBirthDate.includes('/')) {
          const parts = rawBirthDate.split('/');
          if (parts.length === 3) {
            formattedBirthDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          }
        }
      } else if (rawBirthDate._seconds) {
        // Firestore timestamp - use UTC to avoid timezone shift
        const date = new Date(rawBirthDate._seconds * 1000);
        const y = date.getUTCFullYear();
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const d = String(date.getUTCDate()).padStart(2, '0');
        formattedBirthDate = `${y}-${m}-${d}`;
      }
    }

    setSkaterForm({
      firstName: skater.firstName || '',
      lastName: skater.lastName || '',
      membershipId: skater.membershipId || '',
      level: currentLevel,
      birthDate: formattedBirthDate,
      interclubId: skater.interclubId || '',
      clubId: skater.clubId || '',
      club: skater.club || '',
      coaches: skaterCoaches,
      notes: skater.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSaveSkater = async () => {
    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const existingSeasonLevels = selectedSeasonId ? skater?.seasonLevels?.[selectedSeasonId] : null;
      const resolvedStartingLevel = existingSeasonLevels?.startingLevel
        || skater?.startingLevel
        || skater?.currentLevel
        || skaterForm.startingLevel
        || skaterForm.level
        || '';
      const primaryCoach = skaterForm.coaches?.[0] || {};

      const payload = {
        ...skaterForm,
        dateOfBirth: skaterForm.birthDate || skaterForm.dateOfBirth || '',
        currentLevel: skaterForm.level || skaterForm.currentLevel || '',
        startingLevel: resolvedStartingLevel,
        seasonId: selectedSeasonId || '',
        coachName: primaryCoach.displayName || '',
        coachUserId: primaryCoach.coachId || '',
        coachEmail: primaryCoach.email || '',
        ...(skaterForm.level === 'Pre-Preliminary' && prePrelimAcknowledged && !skater?.prePrelimAcknowledgedAt ? {
          prePrelimAcknowledgedAt: new Date().toISOString(),
          prePrelimAcknowledgedBy: user?.uid || null,
          prePrelimAcknowledgedByEmail: user?.email || null,
        } : {}),
      };

      await registrationService.updateSkater(skater.id, payload, token);
      setDialogOpen(false);
      setSuccess('Skater updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      loadSkater();
    } catch (err) {
      setError('Failed to save skater');
      console.error('Error saving skater:', err);
    }
  };

  const handleDeleteSkater = async () => {
    if (!window.confirm('Are you sure you want to delete this skater? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      await registrationService.deleteSkater(skater.id, token);
      navigate('/skaters');
    } catch (err) {
      setError('Failed to delete skater');
      console.error('Error deleting skater:', err);
    }
  };

  const handleInterclubChange = async (event) => {
    const interclubId = event.target.value;
    setSkaterForm((prev) => ({
      ...prev,
      interclubId,
      clubId: '',
      club: '',
    }));
    if (interclubId) {
      await loadClubs(interclubId);
    } else {
      setClubs([]);
    }
  };

  const handleClubChange = (event) => {
    const clubId = event.target.value;
    const selectedClub = clubs.find((club) => club.id === clubId);
    setSkaterForm((prev) => ({
      ...prev,
      clubId,
      club: selectedClub?.name || '',
      interclubId: selectedClub?.interclubId || prev.interclubId,
    }));
  };

  const loadCurrentSeasonResults = async () => {
    if (!skater?.membershipId) {
      setScoreError('Missing USFS ID for this skater.');
      return;
    }

    setScoreLoading(true);
    setScoreError(null);

    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(
        `${baseUrl}/skaters/${skater.membershipId}/scores/${selectedSeasonId}`
      );

      if (!response.ok) {
        throw new Error('Failed to load results');
      }

      const data = await response.json();
      setScoreData(data);
    } catch (err) {
      console.error('Error loading score data:', err);
      setScoreError('Failed to load competition results.');
    } finally {
      setScoreLoading(false);
    }
  };

  const loadPreviousSeasonResults = async () => {
    if (!skater?.membershipId) return;

    const currentSeason = seasons.find(s => s.id === selectedSeasonId);
    if (!currentSeason) return;

    const previousSeasons = seasons
      .filter(s => {
        if (s.id === selectedSeasonId) return false;
        const currentDate = currentSeason.startDate?._seconds
          ? new Date(currentSeason.startDate._seconds * 1000)
          : new Date(currentSeason.startDate);
        const seasonDate = s.startDate?._seconds
          ? new Date(s.startDate._seconds * 1000)
          : new Date(s.startDate);
        return seasonDate < currentDate;
      })
      .sort((a, b) => {
        const aDate = a.startDate?._seconds ? new Date(a.startDate._seconds * 1000) : new Date(a.startDate);
        const bDate = b.startDate?._seconds ? new Date(b.startDate._seconds * 1000) : new Date(b.startDate);
        return bDate - aDate;
      });

    const previousSeason = previousSeasons[0];
    if (!previousSeason) {
      setPreviousSeasonResults(null);
      return;
    }

    setPreviousSeasonLoading(true);

    try {
      const baseUrl = await getApiBaseUrlWithFallback();
      const response = await fetch(
        `${baseUrl}/skaters/${skater.membershipId}/scores/${previousSeason.id}`
      );

      if (response.ok) {
        const data = await response.json();
        setPreviousSeasonResults({
          season: previousSeason,
          data
        });
      } else {
        setPreviousSeasonResults(null);
      }
    } catch (err) {
      console.error('Error loading previous season results:', err);
      setPreviousSeasonResults(null);
    } finally {
      setPreviousSeasonLoading(false);
    }
  };

  const calculateTotalScore = (data) => {
    if (!data?.competitions) return 0;
    const sortedCompetitions = [...data.competitions].sort((a, b) => b.points - a.points);
    const top3 = sortedCompetitions.slice(0, 3);
    return top3.reduce((sum, comp) => sum + comp.points, 0);
  };

  const getSeasonLevels = () => {
    if (!selectedSeasonId || !skater) {
      return {
        startingLevel: skater?.startingLevel,
        currentLevel: skater?.currentLevel,
        levelChanged: false,
      };
    }
    const seasonLevels = skater.seasonLevels?.[selectedSeasonId];
    return {
      startingLevel: seasonLevels?.startingLevel || skater?.startingLevel,
      currentLevel: seasonLevels?.currentLevel || skater?.currentLevel,
      levelChanged: seasonLevels?.levelChanged || false,
    };
  };

  if (loading || authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !skater) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Skater not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/skaters')} sx={{ mt: 2 }}>
          Back to Skaters
        </Button>
      </Box>
    );
  }

  const seasonLevels = getSeasonLevels();
  const isRegistered = registrations.some(r => r.skaterId === skater.id);
  const isPausedForSeason = selectedSeasonId && (skater.pausedSeasons || []).includes(selectedSeasonId);
  const isDisqualifiedForSeason = selectedSeasonId && (skater.disqualifiedSeasons || []).includes(selectedSeasonId);
  const isWithdrawnForSeason = selectedSeasonId && (skater.withdrawnSeasons || []).includes(selectedSeasonId);
  const selectedSeason = seasons.find(s => s.id === selectedSeasonId);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Skater Header */}
      <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              fontWeight: 700,
            }}
          >
            {skater.firstName?.[0]}{skater.lastName?.[0]}
          </Avatar>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              {skater.firstName} {skater.lastName}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              {registrationsLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Chip
                  icon={isRegistered ? <PaymentIcon /> : <CalendarIcon />}
                  label={isRegistered ? 'Registered' : 'Not Registered'}
                  color={isRegistered ? 'success' : 'warning'}
                  size="small"
                  variant={isRegistered ? 'filled' : 'outlined'}
                />
              )}

              <Chip
                label={(() => {
                  if (seasonLevels.levelChanged && seasonLevels.startingLevel && seasonLevels.currentLevel && seasonLevels.startingLevel !== seasonLevels.currentLevel) {
                    return `${seasonLevels.startingLevel} → ${seasonLevels.currentLevel}`;
                  }
                  return seasonLevels.currentLevel || seasonLevels.startingLevel || skater.level || 'Level not set';
                })()}
                size="small"
                variant="outlined"
              />

              {skater.usfsVerified && (
                <Chip
                  icon={<VerifiedIcon />}
                  label="USFS Verified"
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}

              {isDisqualifiedForSeason && (
                <Chip icon={<BlockIcon />} label="Disqualified" size="small" color="error" />
              )}
              {isPausedForSeason && !isDisqualifiedForSeason && (
                <Chip icon={<PauseIcon />} label="Paused" size="small" color="warning" />
              )}
              {isWithdrawnForSeason && !isDisqualifiedForSeason && !isPausedForSeason && (
                <Chip icon={<WithdrawIcon />} label="Withdrawn" size="small" />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', color: 'text.secondary' }}>
              {skater.membershipId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" />
                  <Typography variant="body2">USFS: {skater.membershipId}</Typography>
                </Box>
              )}
              {skater.club && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ClubIcon fontSize="small" />
                  <Typography variant="body2">{skater.club}</Typography>
                </Box>
              )}
              {(skater.coaches?.length > 0 || skater.coachName) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CoachIcon fontSize="small" />
                  <Typography variant="body2">
                    {skater.coaches?.length > 0 
                      ? skater.coaches.map(c => c.displayName || c.name).join(', ')
                      : skater.coachName}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Season</InputLabel>
              <Select
                value={selectedSeasonId}
                label="Season"
                onChange={(e) => setSelectedSeasonId(e.target.value)}
              >
                {seasons.map((season) => (
                  <MenuItem key={season.id} value={season.id}>
                    {season.name || season.year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {!skater.isCoachedSkater && (
              <>
                <IconButton onClick={handleEditSkater} color="primary">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={handleDeleteSkater} color="error">
                  <DeleteIcon />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Registration CTA */}
      {!registrationsLoading && !isRegistered && selectedSeasonId === currentSeasonId && isSeasonRegistrationOpen(selectedSeason) && (
        <Alert
          severity="warning"
          sx={{ mb: 3, borderRadius: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/registration')}>
              Register Now
            </Button>
          }
        >
          This skater is not registered for {selectedSeason?.name || 'the current season'}. Register to track eligibility and participate in competitions.
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Eligibility Tracker" icon={<TrophyIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Current Season" />
          <Tab label="Previous Season" />
          <Tab label="Skater Info" icon={<PersonIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Eligibility Tracker Tab */}
          {selectedTab === 0 && (
            <Box>
              {registrationsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !isRegistered ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main', borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'warning.dark' }}>
                    Not Registered for Current Season
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Register this skater to track eligibility for the championship
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => navigate('/registration')}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                  >
                    Go to Registration
                  </Button>
                </Paper>
              ) : (
                <EligibilityTracker
                  skaterId={skater.id}
                  skaterName={`${skater.firstName} ${skater.lastName}`}
                />
              )}
            </Box>
          )}

          {/* Current Season Results Tab */}
          {selectedTab === 1 && (
            <Box>
              {registrationsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !isRegistered ? (
                <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main', borderRadius: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'warning.dark' }}>
                    Not Registered for Current Season
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Register this skater to participate in competitions
                  </Typography>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => navigate('/registration')}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 999 }}
                  >
                    Go to Registration
                  </Button>
                </Paper>
              ) : (
                <Box>
                  {!scoreData ? (
                    <Button
                      variant="outlined"
                      startIcon={<TrophyIcon />}
                      onClick={loadCurrentSeasonResults}
                      disabled={scoreLoading || !skater.membershipId}
                      fullWidth
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 3, py: 2 }}
                    >
                      {scoreLoading ? 'Loading...' : 'Load Current Season Results'}
                    </Button>
                  ) : (
                    <>
                      {scoreError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{scoreError}</Alert>
                      )}
                      
                      {scoreData.competitions && scoreData.competitions.length > 0 ? (
                        <>
                          <List disablePadding>
                            {scoreData.competitions.map((comp, idx) => (
                              <ListItem
                                key={idx}
                                sx={{
                                  py: 1.5,
                                  px: 2,
                                  mb: 1,
                                  bgcolor: 'grey.50',
                                  borderRadius: 2,
                                }}
                              >
                                <ListItemText
                                  primary={comp.competitionName}
                                  secondary={`${comp.points} points`}
                                  primaryTypographyProps={{ fontWeight: 600 }}
                                />
                                <Chip label={`${comp.points} pts`} size="small" color="primary" />
                              </ListItem>
                            ))}
                          </List>

                          <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 3 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Total Score (Top 3)
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                              {calculateTotalScore(scoreData)} points
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <Alert severity="info">
                          No competitions recorded for this season yet
                        </Alert>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Previous Season Results Tab */}
          {selectedTab === 2 && (
            <Box>
              {previousSeasonLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : previousSeasonResults === null ? (
                <Alert severity="info">
                  No previous season data available
                </Alert>
              ) : previousSeasonResults && previousSeasonResults.data ? (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    {previousSeasonResults.season.name} Results
                  </Typography>

                  {previousSeasonResults.data.competitions && previousSeasonResults.data.competitions.length > 0 ? (
                    <>
                      <List disablePadding>
                        {previousSeasonResults.data.competitions.map((comp, idx) => (
                          <ListItem
                            key={idx}
                            sx={{
                              py: 1.5,
                              px: 2,
                              mb: 1,
                              bgcolor: 'grey.50',
                              borderRadius: 2,
                            }}
                          >
                            <ListItemText
                              primary={comp.competitionName}
                              secondary={`${comp.points} points`}
                              primaryTypographyProps={{ fontWeight: 600 }}
                            />
                            <Chip label={`${comp.points} pts`} size="small" color="primary" />
                          </ListItem>
                        ))}
                      </List>

                      <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Total Score (Top 3)
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                          {calculateTotalScore(previousSeasonResults.data)} points
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Alert severity="info">
                      No competitions recorded for {previousSeasonResults.season.name}
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="info">
                  No previous season results found
                </Alert>
              )}
            </Box>
          )}

          {/* Skater Info Tab */}
          {selectedTab === 3 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    USFS ID
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {skater.membershipId || 'Not provided'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    Club
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {skater.club || 'Not specified'}
                  </Typography>
                </Grid>

                {(skater.coaches?.length > 0 || skater.coachName) && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      {skater.coaches?.length > 1 ? 'Coaches' : 'Coach'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {skater.coaches?.length > 0 ? (
                        skater.coaches.map((coach, idx) => (
                          <Typography key={idx} variant="body1" sx={{ fontWeight: 600 }}>
                            {coach.displayName || coach.name || 'Unknown'}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {skater.coachName}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                {(skater.birthDate || skater.dateOfBirth) && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Birth Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {(() => {
                        const dateStr = skater.birthDate || skater.dateOfBirth;
                        if (typeof dateStr === 'string') {
                          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                            const [year, month, day] = dateStr.split('-');
                            return `${parseInt(month)}/${parseInt(day)}/${year}`;
                          }
                          return dateStr;
                        }
                        if (dateStr?._seconds) {
                          // Firestore timestamp - use UTC to avoid timezone shift
                          const date = new Date(dateStr._seconds * 1000);
                          return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
                        }
                        return 'Not set';
                      })()}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {/* USFS Account Linking Section - Skater-Specific */}
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  {skater.firstName}'s USFS Account
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Link this skater's USFS Members portal credentials to verify their membership and sync test history.
                </Typography>
                
                {usfsError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUsfsError('')}>
                    {usfsError}
                  </Alert>
                )}

                {usfsLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading USFS status...</Typography>
                  </Box>
                ) : usfsStatus?.linked ? (
                  <Box sx={{ p: 2, bgcolor: skater.usfsVerified ? 'success.50' : 'warning.50', borderRadius: 2, border: '1px solid', borderColor: skater.usfsVerified ? 'success.200' : 'warning.200' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          icon={<VerifiedIcon />}
                          label="USFS Account Linked" 
                          size="small" 
                          color="success"
                        />
                        {skater.usfsVerified && (
                          <Chip 
                            label="Verified" 
                            size="small" 
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleManualSync}
                          disabled={usfsLoading}
                        >
                          Sync Now
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleUnlink({ clearData: true })}
                          disabled={unlinking}
                        >
                          {unlinking ? 'Unlinking...' : 'Unlink'}
                        </Button>
                      </Box>
                    </Box>
                    
                    {skater.usfsVerified ? (
                      <>
                        {/* Membership Status Alert - show based on available data */}
                        {/* Show success for USFS members OR LTS members */}
                        {(skater.usfsData?.isCurrentMember === true || skater.usfsData?.isCurrentLTSMember === true) && (
                          <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {skater.usfsData.eligibilityDescription || 'Eligible Member'}
                              {skater.usfsData.isCurrentLTSMember && !skater.usfsData.isCurrentMember && ' (LTS)'}
                              {(skater.usfsData.membershipExpiration || skater.usfsData.ltsEndDate) && (
                                <> — Valid through {skater.usfsData.membershipExpiration || skater.usfsData.ltsEndDate}</>
                              )}
                            </Typography>
                          </Alert>
                        )}
                        {skater.usfsData?.isCurrentMember === false && skater.usfsData?.isCurrentLTSMember !== true && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {skater.usfsData.eligibilityDescription || 'Membership Expired or Invalid'}
                              {skater.usfsData.membershipExpiration && (
                                <> — Was valid through {skater.usfsData.membershipExpiration}</>
                              )}
                            </Typography>
                          </Alert>
                        )}
                        {/* Fallback when isCurrentMember is not available - show verified status */}
                        {skater.usfsData?.isCurrentMember === undefined && skater.usfsData?.membershipStatus && (
                          <Alert 
                            severity={skater.usfsData.membershipStatus === 'active' || skater.usfsData.membershipStatus === 'verified' ? 'success' : 'info'}
                            sx={{ mb: 2 }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {skater.usfsData.membershipStatus === 'verified' 
                                ? 'USFS Account Verified' 
                                : `Membership Status: ${skater.usfsData.membershipStatus.charAt(0).toUpperCase() + skater.usfsData.membershipStatus.slice(1)}`}
                              {skater.usfsData.membershipExpiration && (
                                <> — Valid through {skater.usfsData.membershipExpiration}</>
                              )}
                            </Typography>
                          </Alert>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">USFS Member Number</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData?.membershipId || usfsStatus?.status?.usfsUsername || '—'}</Typography>
                          </Grid>
                          {skater.usfsData?.eligibilityDescription && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Eligibility</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: (skater.usfsData.isCurrentMember || skater.usfsData.isCurrentLTSMember) ? 'success.main' : 'error.main' }}>
                                {skater.usfsData.eligibilityDescription}
                                {skater.usfsData.eligibilityStatus && ` (${skater.usfsData.eligibilityStatus})`}
                              </Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.membershipExpiration && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Membership Valid Through</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData.membershipExpiration}</Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.club && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Home Club</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData.club}</Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.regionName && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Region</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData.regionName}</Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.sectionName && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Section</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData.sectionName}</Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.birthDate && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Birth Date (USFS)</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData.birthDate}</Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.currentLevel && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">USFS Level</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{skater.usfsData.currentLevel}</Typography>
                            </Grid>
                          )}
                          {skater.usfsData?.verifiedAt && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Verified</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(skater.usfsData.verifiedAt).toLocaleDateString()}
                              </Typography>
                            </Grid>
                          )}
                          {usfsStatus?.status?.lastSync && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">Last Synced</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {new Date(usfsStatus.status.lastSync._seconds * 1000).toLocaleDateString()}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </>
                    ) : (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          USFS Account Linked Successfully
                        </Typography>
                        <Typography variant="body2">
                          The skater's name ({skater.firstName} {skater.lastName}) was not automatically matched in this USFS account. 
                          This is normal when using a parent's account. The USFS membership can be verified manually using the 
                          skater's USFS ID: <strong>{skater.membershipId || 'Not set'}</strong>
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'grey.300', textAlign: 'center' }}>
                    <Box
                      component="img"
                      src="/USFig-logo_45d2fa64-d493-4159-8815-4314543b5158.webp"
                      alt="U.S. Figure Skating"
                      sx={{ height: 40, mb: 2, opacity: 0.7 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Connect {skater.firstName}'s USFS Members portal account to verify their membership and automatically sync their test history.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleOpenUSFSLink}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Link {skater.firstName}'s USFS Account
                    </Button>
                  </Box>
                )}
              </Box>

              {skater.notes && (
                <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    NOTES
                  </Typography>
                  <Typography variant="body2">
                    {skater.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Edit Skater Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Skater</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                value={skaterForm.firstName}
                onChange={(e) => setSkaterForm(prev => ({ ...prev, firstName: e.target.value }))}
                fullWidth
                required
              />
              <TextField
                label="Last Name"
                value={skaterForm.lastName}
                onChange={(e) => setSkaterForm(prev => ({ ...prev, lastName: e.target.value }))}
                fullWidth
                required
              />
            </Box>

            <TextField
              label="USFS Membership Number"
              value={skaterForm.membershipId}
              onChange={(e) => setSkaterForm(prev => ({ ...prev, membershipId: e.target.value }))}
              helperText={
                <>
                  Required for scoring.{" "}
                  <Link href="https://www.usfsaonline.org/Account/FindMembershipNumber" target="_blank" rel="noopener noreferrer">
                    Look it up here
                  </Link>
                </>
              }
              required
            />

            <TextField
              label="Birth Date"
              type="date"
              value={skaterForm.birthDate}
              onChange={(e) => setSkaterForm(prev => ({ ...prev, birthDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />

            <FormControl fullWidth>
              <InputLabel>Level</InputLabel>
              <Select
                value={skaterForm.level}
                label="Level"
                onChange={(e) => setSkaterForm(prev => ({ ...prev, level: e.target.value }))}
              >
                {allLevelOptions.map((level) => (
                  <MenuItem key={level} value={level}>{level}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {skaterForm.level === 'Pre-Preliminary' && !skater?.prePrelimAcknowledgedAt && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={prePrelimAcknowledged}
                      onChange={(e) => setPrePrelimAcknowledged(e.target.checked)}
                    />
                  }
                  label="I acknowledge this skater is eligible for Pre-Preliminary level per USFS/ICM rules"
                />
              </Alert>
            )}

            <FormControl fullWidth>
              <InputLabel>Interclub</InputLabel>
              <Select
                value={skaterForm.interclubId}
                label="Interclub"
                onChange={handleInterclubChange}
              >
                <MenuItem value="">Select Interclub</MenuItem>
                {interclubs.map((interclub) => (
                  <MenuItem key={interclub.id} value={interclub.id}>{interclub.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {skaterForm.interclubId && clubs.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Club</InputLabel>
                <Select
                  value={skaterForm.clubId}
                  label="Club"
                  onChange={handleClubChange}
                >
                  <MenuItem value="">Select Club</MenuItem>
                  {clubs.map((club) => (
                    <MenuItem key={club.id} value={club.id}>{club.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Autocomplete
              multiple
              options={coaches}
              getOptionLabel={(option) => option.displayName || option.email || 'Unknown'}
              value={skaterForm.coaches}
              onChange={(e, newValue) => setSkaterForm(prev => ({ ...prev, coaches: newValue }))}
              renderInput={(params) => (
                <TextField {...params} label="Coaches" placeholder="Select coaches" />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option.displayName || option.email}</Typography>
                    {option.email && option.displayName && (
                      <Typography variant="caption" color="text.secondary">{option.email}</Typography>
                    )}
                  </Box>
                </li>
              )}
            />

            <TextField
              label="Notes"
              value={skaterForm.notes}
              onChange={(e) => setSkaterForm(prev => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSkater}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* USFS Link Dialog */}
      <USFSLinkDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        onSubmit={handleUSFSLink}
        loading={linkLoading}
        error={linkError}
        skaterName={skater?.firstName}
        initialUsername={skater?.membershipId || ''}
      />
    </Box>
  );
}
